import polygonClipping, { MultiPolygon, Polygon, Ring } from 'polygon-clipping';
import { AreaGeometry, NormalizedPoint, perimeterInPagePoints, validatePolygon } from './area-geometry';
import { PageSizePoints } from './scale-calibration';

/** Ellipse tessellation keeps area error below 0.05% while remaining inexpensive for live previews. */
export const ELLIPSE_UNION_SEGMENTS = 128;

export interface AreaUnionPolygon {
  /** First ring is the outside boundary; subsequent rings are holes. */
  rings: readonly (readonly NormalizedPoint[])[];
}

export interface AreaUnionSummary {
  grossAreaPoints: number;
  coveredAreaPoints: number;
  overlapAreaPoints: number;
  /** Sum of every shape's own outline, in page points: the perimeter when overlaps count twice. */
  grossPerimeterPoints: number;
  /**
   * Outline of the joined shape, in page points: the perimeter when overlaps count once. Edges
   * that lie inside the joined shape are not part of it; the edges of holes are.
   */
  coveredPerimeterPoints: number;
  polygons: readonly AreaUnionPolygon[];
}

/**
 * Calculate arithmetic and geometric-union totals for valid shapes.
 *
 * Clipping happens in source-page points. Returned boundaries are converted back to normalized
 * page coordinates, so drawing them is independent of browser zoom and canvas resolution.
 */
export function summarizeAreaUnion(geometries: readonly AreaGeometry[], pageSize: PageSizePoints): AreaUnionSummary {
  const geometryPolygons = geometries.map(geometry => geometryToClippingPolygon(geometry, pageSize));
  const polygons = geometryPolygons.flat();
  // Use the same polygonized geometry for gross and covered totals. In particular, this prevents
  // the ellipse tessellation from appearing as a tiny overlap when only one ellipse is present.
  const grossAreaPoints = geometryPolygons.reduce((total, geometry) => total + multiPolygonArea(geometry), 0);
  if (polygons.length === 0) {
    return {
      grossAreaPoints: 0,
      coveredAreaPoints: 0,
      overlapAreaPoints: 0,
      grossPerimeterPoints: 0,
      coveredPerimeterPoints: 0,
      polygons: [],
    };
  }

  const result = polygonClipping.union(polygons[0], ...polygons.slice(1));
  // A union cannot exceed the sum of its parts. Normalize floating error from clipping so
  // disjoint polygonized shapes (including one ellipse) reconcile to zero overlap exactly.
  const rawCoveredAreaPoints = Math.min(grossAreaPoints, multiPolygonArea(result));
  const roundingTolerance = Math.max(1, grossAreaPoints) * Number.EPSILON * 16;
  const coveredAreaPoints =
    grossAreaPoints - rawCoveredAreaPoints <= roundingTolerance ? grossAreaPoints : rawCoveredAreaPoints;
  const grossPerimeterPoints = geometries
    .filter(isValidGeometry)
    .reduce((total, geometry) => total + perimeterInPagePoints(geometry, pageSize), 0);
  // With no overlap the joined outline is just every shape's own outline, so the exact figures are
  // used and a lone oval does not report the slightly shorter outline of its many-sided stand-in.
  const overlaps = grossAreaPoints - coveredAreaPoints > roundingTolerance;
  const coveredPerimeterPoints = overlaps ? multiPolygonPerimeter(result) : grossPerimeterPoints;
  return {
    grossAreaPoints,
    coveredAreaPoints,
    overlapAreaPoints: Math.max(0, grossAreaPoints - coveredAreaPoints),
    grossPerimeterPoints,
    coveredPerimeterPoints,
    polygons: result.map(polygon => ({
      rings: polygon.map(ring => ring.map(([x, y]) => ({ x: x / pageSize.width, y: y / pageSize.height }))),
    })),
  };
}

/** Convert one supported shape to a polygon-clipping polygon in page points. */
function geometryToClippingPolygon(geometry: AreaGeometry, pageSize: PageSizePoints): Polygon[] {
  if (!isValidGeometry(geometry)) return [];

  if (geometry.kind === 'ellipse') {
    const left = Math.min(geometry.start.x, geometry.end.x) * pageSize.width;
    const right = Math.max(geometry.start.x, geometry.end.x) * pageSize.width;
    const top = Math.min(geometry.start.y, geometry.end.y) * pageSize.height;
    const bottom = Math.max(geometry.start.y, geometry.end.y) * pageSize.height;
    const radiusX = (right - left) / 2;
    const radiusY = (bottom - top) / 2;
    if (radiusX === 0 || radiusY === 0) return [];
    const centerX = left + radiusX;
    const centerY = top + radiusY;
    const ring: Ring = [];
    for (let index = 0; index < ELLIPSE_UNION_SEGMENTS; index++) {
      const angle = (index / ELLIPSE_UNION_SEGMENTS) * Math.PI * 2;
      ring.push([centerX + Math.cos(angle) * radiusX, centerY + Math.sin(angle) * radiusY]);
    }
    closeRing(ring);
    return [[ring]];
  }

  if (geometry.kind === 'polygon') {
    const ring = geometry.vertices.map(point => toPagePoint(point, pageSize));
    closeRing(ring);
    return [[ring]];
  }

  const left = Math.min(geometry.start.x, geometry.end.x) * pageSize.width;
  const right = Math.max(geometry.start.x, geometry.end.x) * pageSize.width;
  const top = Math.min(geometry.start.y, geometry.end.y) * pageSize.height;
  const bottom = Math.max(geometry.start.y, geometry.end.y) * pageSize.height;
  if (left === right || top === bottom) return [];
  return [
    [
      [
        [left, top],
        [right, top],
        [right, bottom],
        [left, bottom],
        [left, top],
      ],
    ],
  ];
}

/** Reject zero-area or invalid draft shapes before union calculations. */
function isValidGeometry(geometry: AreaGeometry): boolean {
  if (geometry.kind === 'polygon') return validatePolygon(geometry.vertices).valid;
  return geometry.start.x !== geometry.end.x && geometry.start.y !== geometry.end.y;
}

/** Area of a clipping result, subtracting hole rings from each exterior ring. */
function multiPolygonArea(multiPolygon: MultiPolygon): number {
  return multiPolygon.reduce((total, polygon) => {
    const exterior = Math.abs(signedRingArea(polygon[0] ?? []));
    const holes = polygon.slice(1).reduce((sum, ring) => sum + Math.abs(signedRingArea(ring)), 0);
    return total + Math.max(0, exterior - holes);
  }, 0);
}

/** Signed area of one closed clipping ring. */
function signedRingArea(ring: Ring): number {
  let twiceArea = 0;
  for (let index = 0; index < ring.length - 1; index++) {
    const current = ring[index];
    const next = ring[index + 1];
    twiceArea += current[0] * next[1] - next[0] * current[1];
  }
  return twiceArea / 2;
}

/** Convert one normalized coordinate to source-page points. */
function toPagePoint(point: NormalizedPoint, pageSize: PageSizePoints): [number, number] {
  return [point.x * pageSize.width, point.y * pageSize.height];
}

/** Close a ring in place when the clipping input has distinct first and last points. */
function closeRing(ring: Ring): void {
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) ring.push([...first]);
}

/** Total length of every ring of a clipping result, in the units of its coordinates. */
function multiPolygonPerimeter(multiPolygon: MultiPolygon): number {
  let total = 0;
  for (const polygon of multiPolygon) {
    for (const ring of polygon) {
      // polygon-clipping closes each ring by repeating its first point at the end.
      for (let index = 0; index + 1 < ring.length; index++) {
        total += Math.hypot(ring[index + 1][0] - ring[index][0], ring[index + 1][1] - ring[index][1]);
      }
    }
  }
  return total;
}
